import React from 'react';
import SortBy, {
  SortByItemType,
} from '@/pages/intelligence/components/TableComponents/components/SortBy';
import TableShowData from '@/pages/intelligence/components/TableComponents/components/TableShowData';
import {
  growthFormat,
  showTimeByDateType,
  valueFormat,
  newMathFormat,
} from '@/pages/intelligence/utils/commonUtils';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import { RenderType } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';
import TableChart from '@/pages/intelligence/components/TableComponents/components/TableChart';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { ECHARTSCOLOR } from '@/pages/intelligence/utils/echartsConst';
import { PCConsolePageId } from '../../PCGames/_config/type';
import {
  DATASOURCE_NAME,
  DataSourceBubbleInfo,
} from '@/pages/intelligence/components/DataSourceComponents';
import { getLocale } from 'umi';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import TableImg from '@/pages/intelligence/components/TableComponents/components/TableImg';
import MetaCritic from '@intelligence/pages/TopCharts/components/MetaCritic';
import { decimalFormat, financeFormat, isEmptyValue, ratioValueFormat } from '@home/utils/common';
import TagList from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagList';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import SvgIcon from '@home/components/SvgIcon';
import { processRichText } from '@/pages/intelligence/utils/richTextUtils';
import MetricExplanationTips from '@intelligence/v3/components/MetricExplanationTips';
import { DBI_METRIC_MAP, generateDBIMetricMapKey } from '@intelligence/v3/_config/metrics';
import { buildMetricCompareData } from './metricCompare';

import type { IMetricExplanationTipsClickHandler } from '@intelligence/v3/components/MetricExplanationTips';
import type { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';

const METRICS_TABLE_COLUMN_PADDING = 40; // 与 CloumsCenter 内部 TABLEPADDING 保持一致
/** 指标列宽度下限（固定宽度与自动测算共用） */
const METRICS_TABLE_MIN_COLUMN_WIDTH = 300;

const getMetricCompareValueFormatter = (params: {
  renderType: RenderType;
  maxNumberCal: number;
  isNewMathValue?: boolean;
  key: string;
}): IDataCompareHelper['valueFormatter'] => {
  const { renderType, maxNumberCal, isNewMathValue = false, key } = params;

  return (rawValue) => {
    const numValue = Number(rawValue);

    if (!Number.isFinite(numValue)) {
      return '-';
    }

    if (
      (renderType === RenderType.STEAM_PRICE || renderType === RenderType.PRICE) &&
      numValue === 0
    ) {
      return 'Free';
    }

    if (renderType === RenderType.GROWTH_AND_RATIO) {
      const valueDataObj = growthFormat({
        value: numValue,
        valueType: 'pointValue',
        hasPoint: 'all',
        fixedNum: 2,
        maxNumber: maxNumberCal,
      });

      return String(valueDataObj?.value || '-');
    }

    if (renderType === RenderType.PC_CONSOLE_COMPARE) {
      const isOpinions = ['Steam All reviews Positive%', 'Steam Added Positive%'].includes(key);

      if (isOpinions) {
        return ratioValueFormat(numValue, 2, false, '-', false);
      }

      return financeFormat(numValue, 2, false, '-');
    }

    if (isNewMathValue) {
      return financeFormat(numValue, 2, true, '-');
    }

    return String(valueFormat(numValue, false, 0));
  };
};

const getMetricCompareData = (params: {
  entityType?: string;
  gameName?: string;
  gameCover?: string;
  renderType: RenderType;
  cvm: any;
  key: string;
  metric: any;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  maxNumberCal: number;
  isNewMathValue?: boolean;
}): (IDataCompareData & IDataCompareHelper) | undefined => {
  const {
    cvm,
    metric,
    entityType,
    gameName,
    gameCover,
    dateType,
    startDate,
    endDate,
    renderType,
    maxNumberCal,
    isNewMathValue,
    key,
  } = params;

  return buildMetricCompareData({
    metric,
    entityType: entityType || 'combine',
    title: cvm?.name || cvm?.game_name || cvm?.gametitle || gameName,
    icon: cvm?.cover || cvm?.img || gameCover,
    granularity: dateType,
    startDate,
    endDate,
    valueFormatter: getMetricCompareValueFormatter({
      renderType,
      maxNumberCal,
      isNewMathValue,
      key,
    }),
  });
};

const measureTextWidth = (text: string, font: string): number => {
  if (!text) return 0;
  // SSR/测试环境兜底
  if (typeof document === 'undefined') return text.length * 8;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) return text.length * 8;
  context.font = font;
  return context.measureText(text).width;
};

interface MetricsTableColumnsType {
  key: string;
  loading?: boolean;
  title: string;
  dataSource: string;
  orderDataSource?: string; // 默认
  currentSortKey: string;
  currentSortValue: string; // '' | 'desc' | 'asc';
  onSortChage: (e: SortByItemType) => void;
  sortMetric: string;
  textContent?: any;
  isAvgRankTrend?: any;
  bubTitleRankTrend?: any;
  reqDateObject?: any;
  textContentExtra?: string;
  tipText?: React.ReactNode;
  width?: number | 'auto';
  width_cn?: number | 'auto';
  noWrapWidth?: number | 'auto';
  noWrapWidthCN?: number | 'auto';
  columnLength?: number;
  renderType: RenderType;
  onlySimpleSort?: boolean;
  dateType?: string;
  startDate?: string;
  endDate?: string;
  pageId?: PCConsolePageId;
  isDetailPage?: boolean;
  getMetricValueOfCvm: (cvm: any, metricKey: string, dataSourceKey: string) => any;
  growthValueType?: string;
  isNoDropdown?: boolean;
  formatMessage: Function;
  growthHasPoint?: string;
  textContentSuffix?: string;
  align?: 'left' | 'right' | 'center';
  mauToAvgMau?: boolean;
  icon?: string;
  sortByList?: any[];
  disabledSort?: boolean;
  isNewMathValue?: boolean;
  orderDevice?: string;
  entityType?: string;
  gameName?: string;
  gameCover?: string;
  unitText?: string; // 纯数值情况下，若需要加单位，则用此字段
  columnsCenterWidth?: number | string;
  metricKey?: string;
  dataSourceType?: string;
  svgTipIsNoMore?: boolean;
  svgTipIsNewMore?: boolean;
  bubbleDataSource?: string; // 用于气泡提示中显示的数据源，不影响实际 dataSource
  hideBubbleLeadingIcon?: boolean; // 是否隐藏气泡标题前的数据源 icon
  alignRight?: boolean;
  maxWidth?: number;
  // Compare/PC&ConsoleGamesBeta 页游戏颜色映射
  gamesColorMap?: { [key: string]: string };
  onTagClick?: (type: string, tag: string) => void;
  onMetricExplanationClick?: IMetricExplanationTipsClickHandler;
  onClickViewCalibrationData?: (metric: string) => void;
  showSvgTip?: boolean;
}
// 提取计算国家图表列表的函数
export const calculateCountryChartList = (metric: any) => {
  let sum = 0;
  metric?.history?.forEach((item: any) => {
    sum = sum + item?.value;
  });
  return metric?.history?.map((t: any, id: number) => {
    return {
      title: t?.market,
      img: t?.market === 'Others' ? null : getCountryFlag(t?.market_en),
      num: `${((t?.value / sum) * 100).toFixed(1)}%`,
      color: ECHARTSCOLOR[id],
    };
  });
};

export const MetricsTableCloums = (params: MetricsTableColumnsType) => {
  const {
    key,
    loading,
    title: titleKey,
    textContent: textContentKey,
    textContentExtra: textContentExtraKey,
    tipText: tipTextKey,
    sortMetric,
    dataSource,
    orderDataSource,
    currentSortKey,
    currentSortValue,
    onSortChage,
    renderType,
    width,
    width_cn,
    noWrapWidth,
    noWrapWidthCN,
    columnLength,
    onlySimpleSort,
    dateType,
    startDate,
    endDate,
    pageId,
    isDetailPage = false,
    getMetricValueOfCvm = () => console.log,
    growthValueType,
    isNoDropdown,
    growthHasPoint,
    textContentSuffix: textContentSuffixKey,
    formatMessage = () => console.log,
    align: alignProp,
    mauToAvgMau = true,
    icon,
    sortByList,
    disabledSort,
    orderDevice,
    entityType = 'mobile',
    gameName,
    gameCover,
    columnsCenterWidth,
    unitText,
    dataSourceType = '',
    svgTipIsNoMore,
    svgTipIsNewMore,
    bubbleDataSource,
    hideBubbleLeadingIcon,
    alignRight,
    maxWidth,
    gamesColorMap,
    metricKey,
    onTagClick,
    onMetricExplanationClick,
    onClickViewCalibrationData,
    isNewMathValue = false, // 是否使用新的公共数值处理方法
    isAvgRankTrend,
    bubTitleRankTrend,
    reqDateObject,
    showSvgTip = true,
    ...rest
  } = params;

  const dbiMetricKey = generateDBIMetricMapKey(entityType, dataSource, sortMetric);
  const dbiMetric = DBI_METRIC_MAP[dbiMetricKey];

  // 单独处理 monthly 粒度 ampere 几个显示
  const needProcessMetricsValue = [
    'ampere_d1',
    'ampere_d7',
    'ampere_d14',
    'ampere_d28',
    'ampere_d60',
    'ampere_rolling_d1',
    'ampere_rolling_d7',
    'ampere_rolling_d14',
    'ampere_rolling_d28',
    'ampere_rolling_d60',
  ];

  // console.debug('MetricsTableCloums-params', { params, rest, svgTipIsNewMore, svgTipIsNoMore, titleKey });

  // const { formatMessage } = useIntl();
  const locale = getLocale();

  // const { formatMessage } = useIntlExtend();
  // const localeWidth = locale === LANGUAGE_CN ? width_cn || width : width;

  const isTopchartPlaybyCounty = renderType === RenderType.COUNTRY_CHART && !isDetailPage;
  const isLimitedMetric = ['MetricsTable', 'DigitalRevenue'].includes(pageId || '');
  const maxNumberCal = isLimitedMetric ? 9999 : 99999;
  const maxNumberShow = isLimitedMetric ? '9999+%' : '99999+%';

  /** 当不是环比排序时，表格标题文本有提示文本 */
const showTextBubble = currentSortKey !== `${key}_1`;
  const title = typeof titleKey === 'string' ? formatMessage({ id: titleKey }) : titleKey;

  // 向 formatMessage 传入 br 占位符，然后由 processRichText 方法处理
  const rawTextContent =
    typeof textContentKey === 'string' ? formatMessage({ id: textContentKey }, { br: '{br}' }) : textContentKey;
  const textContent = processRichText(rawTextContent);

  const textContentSuffix = processRichText(
    typeof textContentSuffixKey === 'string'
      ? formatMessage({ id: textContentSuffixKey }, { br: '{br}' })
      : textContentSuffixKey,
  );
  // const  textContent = <>{textContent1}{textContentSuffix}</>
  const tipText = processRichText(
    typeof tipTextKey === 'string' ? formatMessage({ id: tipTextKey }, { br: '{br}' }) : tipTextKey,
  );
  // 仉智琳: 表头跟详情页统一;Topchart表头的MAU改成 Avg MAU;指标名无需修改
  const tableTitle =
    /* isDetailPage &&*/ mauToAvgMau && key === 'mau'
      ? formatMessage({ id: 'dbi_avg_mau' })
      : title;

  // 表头文案宽度处理
  const localeWidth = (() => {
    const isCn = locale === LANGUAGE_CN;
    // 总列数少于 4 列，表头文案不用换行
    const isNoWrap = Number(columnLength) < 4;

    if (isCn && isNoWrap) {
      return noWrapWidthCN || width_cn || width;
    }
    if (isCn) {
      return width_cn || width;
    }
    if (isNoWrap) {
      return noWrapWidth || width;
    }
    return width;
  })();

  const isNoDropdownVal = isNoDropdown || isTopchartPlaybyCounty;

  /**
   * 列宽策略：
   * - 如果传了 width/width_cn（数字），继续使用外部传入的固定宽度
   * - 如果不传，则按"表头一行展示所需宽度"自动计算，避免 tea-table 在缺少 width 时把列压得很窄
   */
  const resolvedColumnWidth = (() => {
    if (typeof localeWidth === 'number')
      return Math.max(localeWidth, METRICS_TABLE_MIN_COLUMN_WIDTH);

    // 仅对 string 标题做精确测量；不做额外"安全预留/最小宽度"，让列宽更贴合内容
    let titleText = typeof tableTitle === 'string' ? tableTitle : '';

    // 当前指标按环比排序时，计算指标名宽度需要加上指标名末尾的"%"符号
    if (currentSortKey === `${key}_1`) {
      titleText = `${titleText}%`;
    }

    // SortBy 样式：font-weight 700, font-size 14px（见 SortBy/index.scss）
    const textWidth = titleText ? measureTextWidth(titleText, '700 14px Arial') : 0;

    // 头部额外占位（经验值，匹配 SortBy 的结构）
    // - 数据源 icon：20px + margin-right 4px
    const hasSourceIcon = Boolean(getDataSourceIcon(icon ? icon : dataSource));
    const sourceIconWidth = hasSourceIcon ? 24 : 0;
    // - sort icon：16px + margin-left 6px
    const hasSortIcon = !isNoDropdownVal;
    const sortIconWidth = hasSortIcon ? 22 : 0;
    // - tipText icon：大约 16px + margin-left 6px
    const tipIconWidth = tipTextKey ? 22 : 0;

    // 这里不再加 safeGap，也不再强制 minWidth，避免右对齐时出现"左侧空白"
    const contentWidth = Math.ceil(textWidth + sourceIconWidth + sortIconWidth + tipIconWidth);
    // 极端兜底：标题为空时给一个最小宽度，避免列塌陷
    const minContentWidth = 24 + 22; // icon + sort icon（大致）

    return Math.max(
      Math.max(contentWidth, minContentWidth) + METRICS_TABLE_COLUMN_PADDING,
      METRICS_TABLE_MIN_COLUMN_WIDTH,
    );
  })();

  const alignLeft =
    isTopchartPlaybyCounty || [RenderType.METACRITIC_SCORE, RenderType.TAGS].includes(renderType);

  const align = alignProp ? alignProp : alignLeft ? 'left' : 'right';

  const onlyValueSort =
    /** 详情页playerbycountry */
    (renderType === RenderType.COUNTRY_CHART && isDetailPage) ||
    [
      RenderType.GROWTH_ONLY,
      RenderType.VALUE_ONLY,
      RenderType.VALUE_ONLY_NO_BMK_UNIT,
      RenderType.GROWTH_ONLY_PERCENTVALUE,
    ].includes(renderType);

  const renderMetricExplanationTips = (() => {
    /**
     * 从系统级维护的指标信息中读取指标信息
     */
    if (!!dbiMetric && !!dbiMetric?.explanation) {
      return (
        <MetricExplanationTips
          entityType={entityType}
          dataSource={dbiMetric?.dataSource}
          dataSourceImg={dbiMetric?.dataSourceImg}
          metric={dbiMetric?.metric}
          data={dbiMetric?.explanation?.en}
          dataCN={dbiMetric?.explanation?.cn}
          onClick={onMetricExplanationClick}
        />
      );
    }
    if (!showTextBubble || !(textContent || tipText || textContentSuffix)) return null;
    return (
      <>
        {textContent ? textContent : null}
        {textContent && tipText && <br />}
        {tipText ? tipText : null}
        {(textContent || tipText) && textContentSuffix && <br />}
        {textContentSuffix ? textContentSuffix : null}
      </>
    );
  })();

  // console.debug('localeWidthlocaleWidth', {
  //   localeWidth,
  //   key,
  //   title,
  //   titleKey,
  //   tableTitle,
  //   textContent,
  //   tipText,
  //   textContentKey,
  //   textContentSuffix,
  // });

  return {
    key: key + title,
    value: key, // 单独 key 暴露出一个 value 字段，用于 metric <-> table col 同步时自动滚动列匹配
    width: resolvedColumnWidth,
    align: alignRight ? 'right' : 'center',
    header: () => {
      return (
        <CloumsCenter width={resolvedColumnWidth} align={align} style={{ whiteSpace: 'nowrap' }}>
          <SortBy
            align={align}
            title={tableTitle}
            disabled={disabledSort}
            defaultKey={currentSortKey}
            defaultValue={currentSortValue === 'asc' || currentSortValue === 'desc' ? currentSortValue : ''}
            svgIcon={getDataSourceIcon(icon ? icon : dataSource)}
            onlySimpleSort={onlySimpleSort}
            isNoDropdown={isNoDropdownVal}
            {...rest}
            list={
              // eslint-disable-next-line no-nested-ternary
              sortByList?.length
                ? sortByList
                : onlyValueSort
                ? [
                    {
                      key: `${key}_0`,
                      sortMetric,
                      orderSource: dataSource,
                      dataSourceNew: orderDataSource,
                      text: title,
                      title: tableTitle,
                      ratioType: 0,
                    },
                  ]
                : [
                    {
                      key: `${key}_0`,
                      sortMetric,
                      orderSource: dataSource,
                      dataSourceNew: orderDataSource,
                      text: title,
                      title: tableTitle,
                      ratioType: 0,
                    },
                    {
                      key: `${key}_1`,
                      sortMetric,
                      orderSource: dataSource,
                      dataSourceNew: orderDataSource,
                      text: `${title}:${formatMessage({
                        id: 'dbi_change',
                      })}(%)`,
                      title: tableTitle?.indexOf('$') > -1 ? `${tableTitle}%` : `${tableTitle}(%)`,
                      ratioType: 1,
                    },
                  ]
            }
            onChange={onSortChage}
            maxWidth={maxWidth}
            svgTip={
              showSvgTip &&
              <DataSourceBubbleInfo
                dataOfDom={
                  <>
                    {dataSource === 'steamcharts' && !hideBubbleLeadingIcon && <img src={getDataSourceIcon(icon ? icon : dataSource)} />}
                    {/* {['D1', 'D2', 'D4', 'D7', 'D14', 'D28'].includes(title)
                      ? `Retention ${title}`
                      : title} */}

                    {/* 旧代码覆盖了 ampere_d1 等其他源的情况，此处进行兼容处理 */}
                    {['D1', 'D2', 'D4', 'D7', 'D14', 'D28'].includes(title) && dataSource !== 'ampere'
                      ? `Retention ${title}`
                      : title}
                  </>
                }
                comesFormDom={
                  <>
                    <img src={getDataSourceIcon(icon ? icon : (bubbleDataSource || dataSource))} />
                    {DATASOURCE_NAME[bubbleDataSource || dataSource]}
                  </>
                }
                metricKey={sortMetric}
                entityType={entityType}
                textContentExtraKey={textContentExtraKey}
                dataSource={
                  dataSource === 'steamcharts' && ['acu', 'pcu'].includes(sortMetric) ? 'vginsights' : dataSource
                }
                isNoMore={dataSource === 'metacritic' || svgTipIsNoMore}
                isNewMore={svgTipIsNewMore}
              />
            }
            content={renderMetricExplanationTips}
            isMetricExplanation={!!dbiMetric || !!onMetricExplanationClick}
            onMetricExplanationClick={onMetricExplanationClick}
            onClickViewCalibrationData={onClickViewCalibrationData}
          />
        </CloumsCenter>
      );
    },
    render: (cvm: any, idx: any) => {
      const sortMetricDataSource = orderDataSource || dataSource || '';
      const metric = getMetricValueOfCvm(cvm, sortMetric, sortMetricDataSource);
      // hour 粒度下使用行级具体时间（cvm.date，含小时）作为环比基准，而非过滤器级别的日期范围
      const isHourlyGranularity = dateType === 'hourly' || dateType === 'hour';
      const effectiveStartDate = isHourlyGranularity ? (cvm?.date || startDate) : startDate;
      const effectiveEndDate = isHourlyGranularity ? (cvm?.date || endDate) : endDate;
      const dataCompareData = getMetricCompareData({
        entityType,
        gameName,
        gameCover,
        renderType,
        cvm,
        key,
        metric,
        dateType,
        startDate: effectiveStartDate,
        endDate: effectiveEndDate,
        maxNumberCal,
        isNewMathValue,
      });

      if (renderType === RenderType.METACRITIC_SCORE) {
        const hasDeviceList = Array.isArray(metric?.history) && metric?.history?.length;
        const list: any[] = [];
        if (hasDeviceList) {
          sortByList?.forEach((sortItem) => {
            const matchedItem = metric?.history?.find((m: any) => m?.device === sortItem?.order_device);
            if (matchedItem) {
              const formattedValue = valueFormat(matchedItem?.value, false, 0);
              const listItem = {
                name: matchedItem?.device,
                value: formattedValue,
              };
              matchedItem?.device === orderDevice ? list.unshift(listItem) : list.push(listItem);
            }
          });
        }
        return (
          <CloumsCenter width={localeWidth} align={align}>
            {!hasDeviceList ? <>-</> : <MetaCritic dataList={list} />}
          </CloumsCenter>
        );
      }

      // Steam Tags & Game Mode Tags 渲染
      if (renderType === RenderType.TAGS) {
        // 优先使用 metricKey（显式传入的字段名），回退到 sortMetric
        const actualMetricKey = (metricKey || sortMetric) as string;
        // 先通过 getMetricValueOfCvm 获取数据（支持自定义取值逻辑）
        const metricData = getMetricValueOfCvm(cvm, actualMetricKey, sortMetricDataSource);
        // 兼容：如果 getMetricValueOfCvm 返回的是包装对象（如 {value: xxx}），取 value；否则直接使用
        const data = metricData?.value !== undefined ? metricData.value : metricData;
        // Handle both array (game_engine) and string (steam_tag, game_mode) formats
        let tags: string[] | undefined;
        if (Array.isArray(data)) {
          tags = data.filter(Boolean);
        } else if (typeof data === 'string' && data.trim()) {
          tags = data.trim().split('|');
        }
        const hasData = tags && tags.length > 0;
        const safeTags: string[] = tags || [];
        const tagType = String(actualMetricKey);
        const safeOnTagClick = onTagClick || (() => undefined);
        return (
          <CloumsCenter width={localeWidth} align={align}>
            {hasData ? (
              <TagList
                key={idx}
                tags={safeTags}
                type={tagType}
                onTagClick={safeOnTagClick}
              />
            ) : (
              '-'
            )}
          </CloumsCenter>
        );
      }

      if (renderType === RenderType.TABLE_SHOW_IMAGE) {
        return (
          <CloumsCenter width={columnsCenterWidth ? columnsCenterWidth : 300} align={align}>
            <div style={{ display: 'flex', alignContent: 'center' }}>
              <TableImg
                loading={loading}
                searchValue={''}
                cvm={metric}
                isNeedMogr={true}
                entityType={entityType}
                // getClick={(i) => window.open(i.url)}
              />
            </div>
          </CloumsCenter>
        );
      }

      if (renderType === RenderType.COUNTRY_CHART) {
        if (isDetailPage) {
          const growthDataObj = growthFormat({
            value: metric?.value,
            valueType: 'percentValue',
            hasPoint: 'all',
            fixedNum: 1,
            maxNumber: maxNumberCal,
          });
          return (
            <CloumsCenter width={localeWidth} align={align}>
              <>{growthDataObj?.value}</>
            </CloumsCenter>
          );
        }
        const list = calculateCountryChartList(metric);
        return !list?.length ? (
          <CloumsCenter width={localeWidth} align={align}>
            <>-</>
          </CloumsCenter>
        ) : (
          <TableChart
            tableData={{
              title: `${formatMessage({
                id: 'dbi_top_asof',
              })} ${showTimeByDateType(metric?.latest_time, dateType || '')}`,
              list,
            }}
          />
        );
      }

      if (renderType === RenderType.STEAM_PRICE || renderType === RenderType.PRICE) {
        const value = valueFormat(metric?.value, false, 0);
        const growthDataObj = growthFormat({
          value: metric?.growth_ratio,
          valueType: 'pointValue',
          hasPoint: 'lessThanOne',
          fixedNum: 2,
          maxNumber: maxNumberCal,
        });
        const showEchart = renderType === RenderType.STEAM_PRICE && !isDetailPage;
        return (
          <CloumsCenter width={localeWidth} align={align}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
            >
              {showEchart && (
                <div
                  style={{ height: 34, width: 120, marginRight: '8px' }}
                  id={`echarts${cvm.app_id}`}
                ></div>
              )}
              <div style={{ width: 128 }}>
                <TableShowData
                  maxNumber={maxNumberShow}
                  value={value === 0 ? 'Free' : value}
                  type={growthDataObj?.type}
                  growth={growthDataObj?.value}
                  dataCompareData={dataCompareData}
                />
              </div>
            </div>
          </CloumsCenter>
        );
      }

      if (renderType === RenderType.VALUE_ONLY) {
        const value = isNewMathValue
          ? financeFormat(metric?.value, 2, true, '-')
          : valueFormat(metric?.value, false, 0);
        return (
          <CloumsCenter width={localeWidth} align={align}>
            <>{value + (unitText && value !== '-' ? unitText : '')}</>
          </CloumsCenter>
        );
      }

      // 仅数值，不展示 BMK 单位，保留两位小数
      if (renderType === RenderType.VALUE_ONLY_NO_BMK_UNIT) {
        const value = decimalFormat(metric?.value, 2, true, '-');
        return (
          <CloumsCenter width={localeWidth} align={align}>
            <>{value + (unitText && value !== '-' ? unitText : '')}</>
          </CloumsCenter>
        );
      }

      if (
        renderType === RenderType.GROWTH_ONLY ||
        renderType === RenderType.GROWTH_ONLY_PERCENTVALUE
      ) {
        const growthDataObj = growthFormat({
          value: metric?.value,
          valueType:
            renderType === RenderType.GROWTH_ONLY_PERCENTVALUE
              ? 'percentValue'
              : growthValueType
              ? growthValueType
              : 'pointValue',
          hasPoint: 'all',
          fixedNum: 2,
          maxNumber: maxNumberCal,
        });
        return (
          <CloumsCenter width={localeWidth} align={align}>
            <>{growthDataObj?.value}</>
          </CloumsCenter>
        );
      }

      if (renderType === RenderType.GROWTH_AND_RATIO) {
        const valueDataObj = growthFormat({
          value: metric?.value,
          valueType: 'pointValue',
          hasPoint: 'all',
          fixedNum: 2,
          maxNumber: maxNumberCal,
        });
        const growthDataObj = growthFormat({
          value: metric?.growth_ratio,
          valueType: 'pointValue',
          hasPoint: 'lessThanOne',
          fixedNum: 2,
          maxNumber: maxNumberCal,
        });

        return (
          <CloumsCenter width={localeWidth} align={align}>
            <TableShowData
              maxNumber={maxNumberShow}
              value={valueDataObj?.value}
              type={growthDataObj?.type}
              growth={growthDataObj?.value}
              dataCompareData={dataCompareData}
            />
          </CloumsCenter>
        );
      }
      if (renderType === RenderType.RANK_AND_RATIO_STORERANK_DETAIL) {
        const value = newMathFormat(metric?.value);
        const linkValue = newMathFormat(metric?.link_value || metric?.linkValue);
        const growthDataObj = growthFormat({
          value: metric?.dod,
          valueType: 'rankValue',
          hasPoint: isAvgRankTrend ? 'all' : 'none',
          fixedNum: 2,
          maxNumber: 99999,
          zeroValue: '-',
        });

        return (
          <CloumsCenter width={localeWidth} align={'right'}>
            <TableShowData
              maxNumber={'99999+%'}
              value={value}
              type={growthDataObj?.type}
              growth={
                <RingRatioBubble
                  title={formatMessage({ id: bubTitleRankTrend })}
                  otherInfo={
                    <div className={'dbi-table-content-right-ratio'}>
                      <div>{formatMessage({ id: 'dbi_rank_change' })}</div>
                      <div
                        className={
                          growthDataObj?.type === 'up' ? `dbi-row-successnum` : `dbi-row-errornum`
                        }
                        style={{ width: '68px', marginRight: '-5px' }}
                      >
                        {growthDataObj?.value}
                        {growthDataObj?.value != '-' ? (
                          <SvgIcon
                            className={`dbi-row-arrow`}
                            type={growthDataObj?.type === 'up' ? 'green-up-new' : 'red-down-new'}
                          />
                        ) : null}
                      </div>
                    </div>
                  }
                  newTime={[reqDateObject?.start_date, reqDateObject?.end_date]}
                  oldTime={[reqDateObject?.link_start_date, reqDateObject?.link_end_date]}
                  oldValue={linkValue}
                  newValue={value}
                  placement={'top-start'}
                >
                  <div className={'dbi-table-content-hot-hover-area'}></div>
                  {growthDataObj?.value}
                </RingRatioBubble>
              }
            />
          </CloumsCenter>
        );
      }

      if (renderType === RenderType.PC_CONSOLE_COMPARE) {
        let value = financeFormat(metric?.value, 2, false, '-');
        const growthDataObj = growthFormat({
          value: metric?.growth_ratio,
          valueType: growthValueType ? growthValueType : 'pointValue',
          hasPoint: growthHasPoint ? growthHasPoint : 'lessThanOne',
          fixedNum: 2,
          maxNumber: maxNumberCal,
        });
        const isOpinions = ['Steam All reviews Positive%', 'Steam Added Positive%'].includes(key);
        if (isOpinions) {
          value = ratioValueFormat(metric?.value, 2, false, '-', false);
        }
        const dWidth = metric?.domWidth ? (metric?.domWidth < 1 ? 1 : metric?.domWidth) : 0;
        return (
          <CloumsCenter width={'100%'} align={align}>
            <TableShowData
              maxNumber={maxNumberShow}
              value={value}
              type={growthDataObj?.type}
              growth={growthDataObj?.value}
              dataCompareData={dataCompareData}
            />
            <div className={'db-new-pc-console'}>
              <div
                className={'db-new-pc-console-table-line'}
                style={{
                  // background: mapColor[val],
                  background: gamesColorMap?.[cvm?.id],
                  width: `${dWidth}%`,
                }}
              ></div>
            </div>
          </CloumsCenter>
        );
      }

      const value = valueFormat(metric?.value, false, 0);
      const growthDataObj = growthFormat({
        value: metric?.growth_ratio,
        valueType: growthValueType ? growthValueType : 'pointValue',
        hasPoint: growthHasPoint ? growthHasPoint : 'lessThanOne',
        fixedNum: 2,
        maxNumber: maxNumberCal,
      });

      return (
        <CloumsCenter width={localeWidth} align={align}>
          <TableShowData
            maxNumber={maxNumberShow}
            value={value}
            type={growthDataObj?.type}
            growth={growthDataObj?.value}
            dataCompareData={dataCompareData}
          />
        </CloumsCenter>
      );
    },
  };
};
